// @ts-nocheck
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/Profile.css";
import { getUser, setUser as persistUser } from "../auth/auth";
import { getReviews } from "../api/reviews";
import {
  fetchUserById,
  fetchUserAchievements,
  relocateUser,
  resetBrowsingLocation,
  updateBrowsingLocation,
  updateProfileLocation,
  updateUserAvatar,
} from "../api/users";
import { getBounties, deleteBounty } from "../api/bounties";
import { getTrades, deleteTrade } from "../api/trades";
import { fetchMyListings } from "../api/listings";
import { performLogout } from "../auth/logout";
// import VerificationModal from "../components/VerificationModal"; // PHASE 2: Re-enable when buying/selling launches
import TopNav from "../components/TopNav";
import AvatarEditModal from "../components/AvatarEditModal";
import PaypalBoostModal from "../components/PaypalBoostModal";
import LocationPickerModal from "../components/LocationPickerModal";
import ProfileAchievementsSection, {
  getProfileAchievementCardClassName,
} from "../components/ProfileAchievementsSection";
import { useToast } from "../components/ToastProvider";
import bountyPlaceholder from "../assets/bounty-placeholder.svg";
import {
  applyFreeBoost,
  getMonetizationMe,
  quoteBoost,
  updateProfileTheme,
} from "../api/monetization";
import {
  formatLocationLabel,
  getBrowsingLocation,
  getBrowsingRadius,
  getProfileLocation,
} from "../utils/location";

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const localUser = getUser();
  const [user, setUser] = useState(localUser);
  const [reviews, setReviews] = useState([]);
  const [achievements, setAchievements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [achievementsError, setAchievementsError] = useState("");
  const [editingLocation, setEditingLocation] = useState(false);
  const [tempLocation, setTempLocation] = useState("");
  const [updateError, setUpdateError] = useState("");
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [activityPage, setActivityPage] = useState(1);
  const [postedTab, setPostedTab] = useState("trades");
  const [postedStatusFilter, setPostedStatusFilter] = useState("all");
  const [postedLoaded, setPostedLoaded] = useState(false);
  const [postedLoading, setPostedLoading] = useState(false);
  const [postedError, setPostedError] = useState("");
  const [postedTrades, setPostedTrades] = useState([]);
  const [postedBounties, setPostedBounties] = useState([]);
  const [postedMarketplaceListings, setPostedMarketplaceListings] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [monetization, setMonetization] = useState(null);
  const [monetizationLoading, setMonetizationLoading] = useState(false);
  const [themeSaving, setThemeSaving] = useState(false);
  const [boostingKey, setBoostingKey] = useState("");
  const [boostCheckoutOpen, setBoostCheckoutOpen] = useState(false);
  const [boostCheckoutTarget, setBoostCheckoutTarget] = useState(null);
  const [boostCheckoutQuote, setBoostCheckoutQuote] = useState(null);
  const [profileLocationModalOpen, setProfileLocationModalOpen] = useState(false);
  const [browsingLocationModalOpen, setBrowsingLocationModalOpen] = useState(false);
  const [relocationModalOpen, setRelocationModalOpen] = useState(false);
  const [pendingProfileLocation, setPendingProfileLocation] = useState(null);
  const [relocationMode, setRelocationMode] = useState("move_all");
  const [relocationSelection, setRelocationSelection] = useState({
    tradeIds: [],
    bountyIds: [],
    listingIds: [],
  });
  const [relocationSaving, setRelocationSaving] = useState(false);
  // const [showVerificationModal, setShowVerificationModal] = useState(false); // PHASE 2: Re-enable
  
  const initials = user?.name
    ? user.name
        .split(" ")
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "CU";
  const profileLocation = getProfileLocation(user);
  const browsingLocation = getBrowsingLocation(user);
  const browsingRadiusMiles = getBrowsingRadius(user);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    if (tab === "plan") setActiveTab("plan");
  }, [location.search]);

  useEffect(() => {
    const fetchData = async () => {
      if (localUser?.id) {
        try {
          const [userData, reviewsData, achievementsData] = await Promise.all([
            fetchUserById(localUser.id),
            getReviews(localUser.id),
            fetchUserAchievements(localUser.id).catch((achievementError) => {
              console.error("Failed to fetch achievements:", achievementError);
              setAchievementsError(achievementError?.message || "Could not load achievements.");
              return null;
            }),
          ]);
          setUser(userData);
          setReviews(reviewsData);
          setAchievements(achievementsData);
          if (achievementsData) {
            setAchievementsError("");
          }
        } catch (error) {
          console.error("Failed to fetch profile data:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    fetchData();
  }, [localUser?.id]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!achievements) return;

    window.requestAnimationFrame(() => {
      if (window.scrollX > 0) {
        window.scrollTo({ left: 0, top: window.scrollY, behavior: "auto" });
      }
    });
  }, [achievements]);

  const loadMonetization = useCallback(async () => {
    if (!user?.id) return;
    setMonetizationLoading(true);
    try {
      const data = await getMonetizationMe();
      setMonetization(data);
      setUser((prev) => ({
        ...(prev || {}),
        activePlanCode: data?.activePlanCode,
        planSelectionCompleted: data?.planSelectionCompleted,
      }));
    } catch (error) {
      console.error("Failed to load monetization data:", error);
    } finally {
      setMonetizationLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (activeTab === "plan" || activeTab === "posted") {
      void loadMonetization();
    }
  }, [activeTab, loadMonetization]);

  const getTrustScore = () => {
    if (!user?.reviewCount) return "Neutral";
    const avgRating = reviews.length > 0 
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length 
      : 5;
    if (avgRating >= 4.8) return "Excellent";
    if (avgRating >= 4.5) return "Very Good";
    if (avgRating >= 4.0) return "Good";
    return "Neutral";
  };

  const getAverageRating = () => {
    if (reviews.length === 0) return 0;
    return (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1);
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} style={{ color: i < fullStars ? "#7cf3ff" : "#444" }}>
          ★
        </span>
      );
    }
    return stars;
  };

  const renderReviewDetails = (review) => {
    const tags = Array.isArray(review?.tags) ? review.tags : [];
    const hasComment = Boolean(String(review?.comment || "").trim());

    return (
      <>
        {review?.autoGenerated && (
          <div className="review-badge">Auto-finalized after 48h</div>
        )}
        {tags.length > 0 && (
          <div className="review-tags">
            {tags.map((tag) => (
              <span key={tag} className="review-tag">
                {tag}
              </span>
            ))}
          </div>
        )}
        {hasComment && <div className="review-comment">{review.comment}</div>}
      </>
    );
  };

  const getReviewerDisplayName = useCallback((review) => {
    const name = String(review?.reviewerName || "").trim();
    return name || "Trader";
  }, []);

  const getReviewerInitial = useCallback((review) => {
    return getReviewerDisplayName(review).charAt(0).toUpperCase() || "T";
  }, [getReviewerDisplayName]);

  const formatReviewDate = useCallback((review) => {
    if (!review?.date) return "";
    const date = new Date(review.date);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  const maskCounterparty = (name) => {
    if (!name) return "Trader";
    const trimmed = name.trim();
    if (!trimmed) return "Trader";
    return `${trimmed.charAt(0).toUpperCase()}***`;
  };

  const activityItems = useMemo(() => {
    return reviews.map((review, index) => ({
      id: review.id || `review-${index}`,
      date: review.date,
      rating: review.rating,
      comment: review.comment,
      counterparty: maskCounterparty(review.reviewerName),
    }));
  }, [reviews]);

  const achievementsSection = (
    <ProfileAchievementsSection
      achievements={achievements}
      loading={loading}
      error={!loading ? achievementsError : ""}
    />
  );

  const activityPageSize = 5;
  const visibleActivity = activityItems.slice(0, activityPage * activityPageSize);

  // PHASE 2: Functions moved to AccountMenu component
  // handleLogout, handleAccountDeletion, handleVerificationSubmit

  const applyUserUpdate = useCallback((nextUser) => {
    setUser(nextUser);
    persistUser(nextUser);
  }, []);

  const handleLogout = () => {
    performLogout();
  };

  const handleAvatarSave = async (blob) => {
    if (!user?.id) {
      showToast("Please log in to update your photo.", "error");
      return;
    }

    try {
      await updateUserAvatar(user.id, blob);
      const refreshedUser = await fetchUserById(user.id);
      applyUserUpdate(refreshedUser);
      showToast("Profile photo updated.", "success");
      setShowAvatarModal(false);
    } catch (error) {
      console.error("Failed to update avatar:", error);
      showToast(error.message || "Could not update your photo.", "error");
    }
  };

  const toTime = useCallback((value) => {
    if (!value) return null;
    const t = new Date(value).getTime();
    return Number.isFinite(t) ? t : null;
  }, []);

  const formatLifecycleDate = useCallback((value) => {
    if (!value) return "Not available";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  const getSubscriptionStatusLabel = useCallback((subscription) => {
    const status = String(subscription?.subscriptionStatus || "").toLowerCase();
    if (subscription?.cancelAtPeriodEnd) return "Pending downgrade";
    if (status === "active") return "Active";
    if (status === "past_due") return "Past due";
    if (status === "suspended") return "Suspended";
    if (status === "cancelled") return "Cancelled";
    if (status === "expired") return "Expired";
    if (status === "pending") return "Pending";
    return "Manual";
  }, []);

  const getSubscriptionStatusClass = useCallback((subscription) => {
    if (subscription?.cancelAtPeriodEnd) return "scheduled";
    const status = String(subscription?.subscriptionStatus || "").toLowerCase();
    if (status === "active") return "active";
    if (status === "past_due") return "past-due";
    if (status === "suspended") return "suspended";
    if (status === "cancelled" || status === "expired") return "cancelled";
    if (status === "pending") return "pending";
    return "manual";
  }, []);

  const getDisplayPostedStatus = useCallback((item) => {
    const status = String(item?.status || "").toLowerCase();
    if (status === "cancelled") return "cancelled";
    if (status === "completed") return "completed";
    const expiresAt = toTime(item?.expiresAt);
    const boostExpiresAt = toTime(item?.boostExpiresAt);
    const effectiveEnd = Math.max(expiresAt || 0, boostExpiresAt || 0);
    if (effectiveEnd > 0 && effectiveEnd < Date.now()) return "expired";
    return "active";
  }, [toTime]);

  const isPostedItemActive = useCallback((item) => getDisplayPostedStatus(item) === "active", [getDisplayPostedStatus]);

  const isPostedItemBoosted = useCallback((item) => {
    const boostExpiresAt = toTime(item?.boostExpiresAt);
    return !!boostExpiresAt && boostExpiresAt > Date.now();
  }, [toTime]);

  const formatBoostEndDate = useCallback((value) => {
    if (!value) return "Not available";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Not available";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, []);

  const sortPostedItems = useCallback((items) => {
    const rank = { active: 0, completed: 1, expired: 2, cancelled: 3 };
    return [...items].sort((a, b) => {
      const aStatus = getDisplayPostedStatus(a);
      const bStatus = getDisplayPostedStatus(b);
      const aRank = rank[aStatus] ?? 99;
      const bRank = rank[bStatus] ?? 99;
      if (aRank !== bRank) return aRank - bRank;
      const at = new Date(a?.createdAt || 0).getTime();
      const bt = new Date(b?.createdAt || 0).getTime();
      return bt - at;
    });
  }, [getDisplayPostedStatus]);

  const loadPostedListings = useCallback(async () => {
    if (!user?.id) return;
    setPostedLoading(true);
    setPostedError("");
    try {
      const [trades, bounties, listings] = await Promise.all([
        getTrades({ userId: user.id, status: "all", limit: 100, offset: 0 }),
        getBounties({ userId: user.id, status: "all", limit: 100, offset: 0 }),
        fetchMyListings(),
      ]);

      setPostedTrades(sortPostedItems(trades?.items || []));
      setPostedBounties(sortPostedItems(bounties?.items || []));
      setPostedMarketplaceListings(sortPostedItems(listings || []));
      setPostedLoaded(true);
    } catch (error) {
      console.error("Failed to fetch posted listings:", error);
      setPostedError(error?.message || "Could not load your posted listings.");
    } finally {
      setPostedLoading(false);
    }
  }, [user?.id, sortPostedItems]);

  const activeRelocationTrades = useMemo(
    () => postedTrades.filter((item) => getDisplayPostedStatus(item) === "active"),
    [postedTrades, getDisplayPostedStatus],
  );
  const activeRelocationBounties = useMemo(
    () => postedBounties.filter((item) => getDisplayPostedStatus(item) === "active"),
    [postedBounties, getDisplayPostedStatus],
  );
  const activeRelocationListings = useMemo(
    () => postedMarketplaceListings.filter((item) => String(item?.status || "").toLowerCase() === "active"),
    [postedMarketplaceListings],
  );

  const toggleRelocationSelection = useCallback((group, id) => {
    setRelocationSelection((prev) => {
      const nextValues = prev[group].includes(id)
        ? prev[group].filter((value) => value !== id)
        : [...prev[group], id];
      return { ...prev, [group]: nextValues };
    });
  }, []);

  const handleBrowsingSave = useCallback(async (location, radiusMiles) => {
    if (!user?.id) return;
    const updatedUser = await updateBrowsingLocation(user.id, location, radiusMiles);
    applyUserUpdate(updatedUser);
  }, [applyUserUpdate, user?.id]);

  const handleBrowsingReset = useCallback(async () => {
    if (!user?.id) return;
    const updatedUser = await resetBrowsingLocation(user.id, browsingRadiusMiles);
    applyUserUpdate(updatedUser);
  }, [applyUserUpdate, browsingRadiusMiles, user?.id]);

  const handleProfileLocationPicked = useCallback(async (location) => {
    if (!user?.id) return;
    await loadPostedListings();
    setPendingProfileLocation(location);
    setRelocationSelection({
      tradeIds: activeRelocationTrades.map((item) => item.id),
      bountyIds: activeRelocationBounties.map((item) => item.id),
      listingIds: activeRelocationListings.map((item) => item.id),
    });
    setRelocationMode("move_all");
    setRelocationModalOpen(true);
  }, [activeRelocationBounties, activeRelocationListings, activeRelocationTrades, loadPostedListings, user?.id]);

  const submitRelocation = useCallback(async () => {
    if (!user?.id || !pendingProfileLocation) return;
    setRelocationSaving(true);
    try {
      const response = await relocateUser(user.id, {
        profileLocation: pendingProfileLocation,
        mode: relocationMode,
        selection: relocationMode === "select_posts" ? relocationSelection : undefined,
      });
      applyUserUpdate(response?.user || response);
      setRelocationModalOpen(false);
      setPendingProfileLocation(null);
      await loadPostedListings();
      showToast("Location settings updated.", "success");
    } catch (error) {
      showToast(error?.message || "Could not update your location settings.", "error");
    } finally {
      setRelocationSaving(false);
    }
  }, [
    applyUserUpdate,
    loadPostedListings,
    pendingProfileLocation,
    relocationMode,
    relocationSelection,
    showToast,
    user?.id,
  ]);

  const buildStatusCounts = useCallback((items) => {
    const counts = { all: items.length, active: 0, completed: 0, expired: 0, cancelled: 0 };
    for (const item of items) {
      const status = getDisplayPostedStatus(item);
      counts[status] = (counts[status] || 0) + 1;
    }
    return counts;
  }, [getDisplayPostedStatus]);

  const filteredPostedTrades = useMemo(() => {
    if (postedStatusFilter === "all") return postedTrades;
    return postedTrades.filter((item) => getDisplayPostedStatus(item) === postedStatusFilter);
  }, [postedTrades, postedStatusFilter, getDisplayPostedStatus]);

  const filteredPostedBounties = useMemo(() => {
    if (postedStatusFilter === "all") return postedBounties;
    return postedBounties.filter((item) => getDisplayPostedStatus(item) === postedStatusFilter);
  }, [postedBounties, postedStatusFilter, getDisplayPostedStatus]);

  const tradeStatusCounts = useMemo(() => buildStatusCounts(postedTrades), [postedTrades, buildStatusCounts]);
  const bountyStatusCounts = useMemo(() => buildStatusCounts(postedBounties), [postedBounties, buildStatusCounts]);

  useEffect(() => {
    if (activeTab === "posted" && !postedLoaded && !postedLoading) {
      loadPostedListings();
    }
  }, [activeTab, postedLoaded, postedLoading, loadPostedListings]);

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteModalOpen(false);
    setDeleteTarget(null);
    setDeleteError("");
  };

  const openDeleteModal = (type, item) => {
    if (!isPostedItemActive(item)) {
      showToast("Only active posts can be deleted from here.", "error");
      return;
    }
    setDeleteError("");
    setDeleteTarget({
      type,
      id: item.id,
      title: item.cardName || "this listing",
    });
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    setDeleteError("");
    try {
      if (deleteTarget.type === "trade") {
        await deleteTrade(deleteTarget.id);
        setPostedTrades((prev) => prev.filter((item) => item.id !== deleteTarget.id));
        showToast("Trade deleted.", "success");
      } else {
        await deleteBounty(deleteTarget.id);
        setPostedBounties((prev) => prev.filter((item) => item.id !== deleteTarget.id));
        showToast("Bounty deleted.", "success");
      }
      setDeleteModalOpen(false);
      setDeleteTarget(null);
    } catch (error) {
      setDeleteError(error?.message || "Failed to delete listing.");
      showToast(error?.message || "Failed to delete listing.", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePostedCardKeyDown = (event, onActivate) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onActivate();
    }
  };

  const openTradeHubForPost = useCallback((postType, postId) => {
    const source = postType === "trade" ? postedTrades : postedBounties;
    const target = (source || []).find((item) => String(item?.id) === String(postId));
    if (!target || !isPostedItemActive(target)) {
      showToast("Only active posts have live matches.", "error");
      return;
    }
    navigate(`/trades?postType=${encodeURIComponent(postType)}&postId=${encodeURIComponent(postId)}`);
  }, [isPostedItemActive, navigate, postedBounties, postedTrades, showToast]);

  const handleThemeChange = async (theme) => {
    setThemeSaving(true);
    try {
      await updateProfileTheme(theme);
      showToast("Theme updated.", "success");
      await loadMonetization();
    } catch (error) {
      showToast(error?.message || "Failed to update theme.", "error");
    } finally {
      setThemeSaving(false);
    }
  };

  const isBoostTimeoutError = (error) => {
    const status = Number(error?.status || 0);
    const code = String(error?.code || "").toLowerCase();
    const message = String(error?.message || "").toLowerCase();
    return (
      status === 0 ||
      code.includes("timeout") ||
      message.includes("timeout") ||
      code.includes("ecconnaborted")
    );
  };

  const closeBoostCheckout = useCallback(() => {
    setBoostCheckoutOpen(false);
    setBoostCheckoutTarget(null);
    setBoostCheckoutQuote(null);
  }, []);

  const handleBoostCheckoutSuccess = useCallback(
    async (result) => {
      showToast(result?.message || "Boost applied successfully.", "success");
      await loadPostedListings();
      await loadMonetization();
      closeBoostCheckout();
    },
    [closeBoostCheckout, loadMonetization, loadPostedListings, showToast],
  );

  const handleBoost = async (postType, postId) => {
    const source = postType === "trade" ? postedTrades : postedBounties;
    const target = (source || []).find((item) => String(item?.id) === String(postId));
    if (!target || getDisplayPostedStatus(target) !== "active") {
      showToast("Only active posts can be boosted.", "error");
      return;
    }

    const key = `${postType}:${postId}`;
    setBoostingKey(key);
    try {
      const quote = await quoteBoost({ postType, postId });
      if (quote.isFree) {
        await applyFreeBoost({ postType, postId });
        showToast("Boost applied for free.", "success");
        await loadPostedListings();
        await loadMonetization();
        return;
      }
      setBoostCheckoutQuote(quote);
      setBoostCheckoutTarget({
        postType,
        postId,
        title: target?.cardName || target?.title || "this listing",
      });
      setBoostCheckoutOpen(true);
    } catch (error) {
      if (isBoostTimeoutError(error)) {
        showToast(
          "PayPal is taking too long to start checkout. Please try again.",
          "warning",
        );
        return;
      }
      showToast(error?.message || "Failed to boost post.", "error");
    } finally {
      setBoostingKey("");
    }
  };

  return (
    <>
      <TopNav />
      <div className="profile-screen page-with-top-nav">
        <div
          className={[
            "profile-card",
            getProfileAchievementCardClassName(achievements?.display?.profileBorderTone || "none"),
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <header className="profile-header">
            <div>
              <h1>Your Trader Profile</h1>
            </div>
          </header>

        <div className="profile-body">
          <button
            type="button"
            className="avatar-button"
            onClick={() => setShowAvatarModal(true)}
            aria-label="Edit profile photo"
          >
            {user?.avatar ? (
              <img src={user.avatar} alt="Profile avatar" className="avatar-image" />
            ) : (
              <span className="avatar-initials">{initials}</span>
            )}
            <span className="avatar-edit-overlay">Edit</span>
          </button>
          <div>
            <h2>{user?.name || "Cardex Trader"}</h2>
            <p className="profile-subtitle">Member since {new Date(user?.createdAt || Date.now()).getFullYear()}</p>
          </div>
        </div>

        <div className="profile-tabs" role="tablist" aria-label="Profile tabs">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "overview"}
            className={`profile-tab ${activeTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTab("overview")}
          >
            Overview
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "reviews"}
            className={`profile-tab ${activeTab === "reviews" ? "active" : ""}`}
            onClick={() => setActiveTab("reviews")}
          >
            Reviews
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "activity"}
            className={`profile-tab ${activeTab === "activity" ? "active" : ""}`}
            onClick={() => setActiveTab("activity")}
          >
            Activity
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "posted"}
            className={`profile-tab ${activeTab === "posted" ? "active" : ""}`}
            onClick={() => setActiveTab("posted")}
          >
            Posted
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "plan"}
            className={`profile-tab ${activeTab === "plan" ? "active" : ""}`}
            onClick={() => setActiveTab("plan")}
          >
            Plan & Usage
          </button>
        </div>

        {activeTab === "overview" && (
          <>

        <div className="location-section">
          <div className="location-header">
            <div>
              <div className="location-label">Profile Location</div>
              <div className="location-value">
                {formatLocationLabel(profileLocation)}
              </div>
              <div className="subtext">This is your home market. Changing it can move active posts if you choose.</div>
            </div>
            <button className="btn-edit-location" onClick={() => setProfileLocationModalOpen(true)}>
              {profileLocation ? "Change" : "Set Location"}
            </button>
          </div>
        </div>

        <div className="location-section">
          <div className="location-header">
            <div>
              <div className="location-label">Browsing In</div>
              <div className="location-value">
                {formatLocationLabel(browsingLocation)}
              </div>
              <div className="subtext">Discovery follows this area. Your cards do not.</div>
            </div>
            <div className="location-actions">
              <button className="btn-edit-location" onClick={() => setBrowsingLocationModalOpen(true)}>
                Change
              </button>
              {profileLocation ? (
                <button className="btn-cancel" onClick={() => void handleBrowsingReset()}>
                  Reset to Profile
                </button>
              ) : null}
            </div>
          </div>
          <div className="subtext">Radius: {browsingRadiusMiles} miles</div>
        </div>

        <div className="location-section">
          <div className="location-header">
            <div>
              <div className="location-label">Relocate Account</div>
              <div className="subtext">Move your home market without forcing all cards to move with you.</div>
            </div>
            <button className="btn-edit-location" onClick={() => setProfileLocationModalOpen(true)}>
              Start Relocation
            </button>
          </div>
        </div>

        {/* Trust & Reputation Section */}
        <div className="trust-section">
          <h3 className="section-title">Trust & Reputation</h3>
          
          <div className="trust-stats">
            <div className="stat-card">
              <div className="stat-label">Trust Score</div>
              <div className="stat-value trust-score">{getTrustScore()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Average Rating</div>
              <div className="stat-value">
                {getAverageRating()} / 5.0
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Reviews</div>
              <div className="stat-value">{user?.reviewCount || 0}</div>
            </div>
          </div>

          <div className="trust-stats">
            <div className="stat-card">
              <div className="stat-label">Trade Cards Posted</div>
              <div className="stat-value">{user?.itemsListed || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Successful Trades</div>
              <div className="stat-value">{user?.salesCount || 0}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Community Trust</div>
              <div className="stat-value verified-status">
                {user?.verified ? (
                  <>
                    <span className="verified-icon">✔</span>
                    <span>Good Standing</span>
                  </>
                ) : (
                  <span style={{ opacity: 0.6 }}>Neutral</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Verification Section - PHASE 2 (hidden for now) */}
        {/*
        <div className="verification-section">
          <h3 className="section-title">Verified Trader (Phase 2)</h3>

          <div className="verification-status phase2-placeholder">
            <div className="status-icon">🔜</div>
            <div>
              <div className="status-title">Identity Verification (Coming in Phase 2)</div>
              <div className="status-subtitle">
                Optional verification will be available when buying/selling launches.
                For now, enjoy local, in-person trading with no payments or shipping.
              </div>
            </div>
          </div>
        </div>
        */}

        {/* PHASE 2: DORMANT VERIFICATION CODE BELOW - DO NOT DELETE */}
        {/* 
        <div className="verification-section">
          <h3 className="section-title">Seller Verification</h3>
          
          {user?.verificationStatus === "verified" ? (
            <div className="verification-status verified">
              <div className="verification-badge">
                <span className="badge-icon">✓</span>
                <div>
                  <div className="badge-title">Verified Seller</div>
                  <div className="badge-subtitle">Identity confirmed by Cardex</div>
                </div>
              </div>
              <div className="verification-benefits">
                <div className="benefit-item">✓ Priority in search results</div>
                <div className="benefit-item">✓ Verified badge on all listings</div>
                <div className="benefit-item">✓ Increased buyer trust</div>
                <div className="benefit-item">✓ Access to promoted listings (Premium)</div>
              </div>
            </div>
          ) : user?.verificationStatus === "pending" ? (
            <div className="verification-status pending">
              <div className="status-icon">⏳</div>
              <div>
                <div className="status-title">Verification Pending</div>
                <div className="status-subtitle">
                  We're reviewing your verification documents. This usually takes 24-48 hours.
                </div>
              </div>
            </div>
          ) : user?.verificationStatus === "rejected" ? (
            <div className="verification-status rejected">
              <div className="status-icon">✕</div>
              <div>
                <div className="status-title">Verification Rejected</div>
                <div className="status-subtitle">
                  {user?.verificationRejectionReason || "Your verification was rejected. Please try again with valid documents."}
                </div>
              </div>
              <button 
                className="btn-verify" 
                onClick={() => setShowVerificationModal(true)}
              >
                Try Again
              </button>
            </div>
          ) : (
            <div className="verification-status unverified">
              <div className="status-content">
                <div className="status-icon">🔒</div>
                <div>
                  <div className="status-title">Not Verified</div>
                  <div className="status-subtitle">
                    Verify your identity to build trust and boost your sales.
                    {user?.verificationRequired && (
                      <div className="verification-required-notice">
                        ⚠️ Verification required due to user reports. You must verify to continue selling.
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="verification-benefits">
                <div className="benefit-item">✓ Priority placement in search</div>
                <div className="benefit-item">✓ Verified badge on listings</div>
                <div className="benefit-item">✓ Higher buyer confidence</div>
                <div className="benefit-item">✓ Access to premium features</div>
              </div>
              <button 
                className="btn-verify" 
                onClick={() => setShowVerificationModal(true)}
              >
                {user?.verificationRequired ? "Verify Now (Required)" : "Get Verified"}
              </button>
            </div>
          )}
        </div>
        */}

        {/* Reviews Section */}
        <div className="reviews-section">
          <div className="reviews-header">
            <h3 className="section-title">Recent Reviews (Preview)</h3>
            <button
              type="button"
              className="reviews-link"
              onClick={() => setActiveTab("reviews")}
            >
              View all reviews →
            </button>
          </div>
          
          {loading ? (
            <div className="loading-state">Loading reviews...</div>
          ) : reviews.length === 0 ? (
            <div className="empty-state">No reviews yet</div>
          ) : (
            <div className="reviews-list">
              {reviews.slice(0, 3).map((review) => (
                <div key={review.id} className="review-card">
                  <div className="review-header">
                    <div className="reviewer-info">
                      <div className="reviewer-avatar">
                        {getReviewerInitial(review)}
                      </div>
                      <div>
                        <div className="reviewer-name">{getReviewerDisplayName(review)}</div>
                        <div className="review-date">{formatReviewDate(review)}</div>
                      </div>
                    </div>
                    <div className="review-rating">
                      {renderStars(review.rating)}
                    </div>
                  </div>
                  {renderReviewDetails(review)}
                </div>
              ))}
            </div>
          )}
        </div>

        {achievementsSection}

        <div className="posted-cta-wrap">
          <button
            type="button"
            className="btn-secondary posted-cta-button"
            onClick={() => setActiveTab("posted")}
          >
            View Posted Trades
          </button>
        </div>

        {/* PHASE 1: Legal & Account sections moved to hamburger menu (☰) in navigation */}
        {/* All Legal & Safety items are now in AccountMenu component */}

        <div className="profile-actions">
          <button className="btn-secondary" type="button" onClick={() => navigate("/home")}>
            Back to Home
          </button>
          <button className="btn-logout" type="button" onClick={handleLogout} style={{marginLeft: 12}}>
            Log Out
          </button>
        </div>
          </>
        )}

        {activeTab === "reviews" && (
          <>
            <div className="reviews-section">
              <h3 className="section-title">Recent Reviews ({reviews.length})</h3>

              {loading ? (
                <div className="loading-state">Loading reviews...</div>
              ) : reviews.length === 0 ? (
                <div className="empty-state">No reviews yet</div>
              ) : (
                <div className="reviews-list">
                  {reviews.map((review) => (
                    <div key={review.id} className="review-card">
                      <div className="review-header">
                        <div className="reviewer-info">
                          <div className="reviewer-avatar">
                            {getReviewerInitial(review)}
                          </div>
                          <div>
                            <div className="reviewer-name">{getReviewerDisplayName(review)}</div>
                            <div className="review-date">{formatReviewDate(review)}</div>
                          </div>
                        </div>
                        <div className="review-rating">
                          {renderStars(review.rating)}
                        </div>
                      </div>
                      {renderReviewDetails(review)}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {achievementsSection}
          </>
        )}

        {activeTab === "activity" && (
          <div className="activity-section">
            <h3 className="section-title">Trade Activity</h3>
            {loading ? (
              <div className="loading-state">Loading activity...</div>
            ) : activityItems.length === 0 ? (
              <div className="empty-state">No completed trades yet</div>
            ) : (
              <>
                <div className="activity-list">
                  {visibleActivity.map((activity) => (
                    <div key={activity.id} className="activity-card">
                      <div className="activity-header">
                        <div>
                          <div className="activity-title">Trade completed</div>
                          <div className="activity-meta">
                            With {activity.counterparty} · {new Date(activity.date).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </div>
                        </div>
                        <div className="activity-rating">
                          {renderStars(activity.rating)}
                        </div>
                      </div>
                      <div className="activity-comment">{activity.comment}</div>
                    </div>
                  ))}
                </div>
                {visibleActivity.length < activityItems.length && (
                  <button
                    type="button"
                    className="btn-secondary activity-load-more"
                    onClick={() => setActivityPage((prev) => prev + 1)}
                  >
                    Load more
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === "posted" && (
          <div className="posted-section">
            <div className="posted-header">
              <h3 className="section-title">Your Posted Listings</h3>
              <button
                type="button"
                className="reviews-link"
                onClick={loadPostedListings}
                disabled={postedLoading}
              >
                {postedLoading ? "Refreshing..." : "Refresh"}
              </button>
            </div>

            <div className="posted-subtabs" role="tablist" aria-label="Posted listing tabs">
              <button
                type="button"
                role="tab"
                aria-selected={postedTab === "trades"}
                className={`posted-subtab ${postedTab === "trades" ? "active" : ""}`}
                onClick={() => setPostedTab("trades")}
              >
                My Trades
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={postedTab === "bounties"}
                className={`posted-subtab ${postedTab === "bounties" ? "active" : ""}`}
                onClick={() => setPostedTab("bounties")}
              >
                My Bounties
              </button>
            </div>

            {!postedLoading && !postedError && (
              <div className="posted-filter-chips" role="tablist" aria-label="Posted status filters">
                {[
                  { key: "all", label: "All" },
                  { key: "active", label: "Active" },
                  { key: "completed", label: "Completed" },
                  { key: "expired", label: "Expired" },
                  { key: "cancelled", label: "Cancelled" },
                ].map((filter) => {
                  const counts = postedTab === "trades" ? tradeStatusCounts : bountyStatusCounts;
                  return (
                    <button
                      key={filter.key}
                      type="button"
                      role="tab"
                      aria-selected={postedStatusFilter === filter.key}
                      className={`posted-filter-chip ${postedStatusFilter === filter.key ? "active" : ""}`}
                      onClick={() => setPostedStatusFilter(filter.key)}
                    >
                      {filter.label} ({counts?.[filter.key] || 0})
                    </button>
                  );
                })}
              </div>
            )}

            {postedLoading && <div className="loading-state">Loading your listings...</div>}
            {!postedLoading && postedError && (
              <div className="posted-error">
                <span>{postedError}</span>
                <button type="button" className="btn-secondary" onClick={loadPostedListings}>
                  Retry
                </button>
              </div>
            )}

            {!postedLoading && !postedError && postedTab === "trades" && (
              <>
                {filteredPostedTrades.length === 0 ? (
                  <div className="empty-state">
                    {postedStatusFilter === "all"
                      ? "You haven’t posted any trades yet."
                      : `No ${postedStatusFilter} trades.`}
                  </div>
                ) : (
                  <div className="posted-list">
                    {filteredPostedTrades.map((trade) => {
                      const displayStatus = getDisplayPostedStatus(trade);
                      const isActive = isPostedItemActive(trade);
                      const isBoosted = isActive && isPostedItemBoosted(trade);
                      return (
                      <div
                        key={trade.id}
                        className="posted-card posted-card--trade"
                        onClick={() => {
                          if (!isActive) return;
                          navigate(`/trade-offer/${trade.id}`);
                        }}
                        role={isActive ? "button" : undefined}
                        tabIndex={isActive ? 0 : -1}
                        onKeyDown={(e) =>
                          isActive ? handlePostedCardKeyDown(e, () => navigate(`/trade-offer/${trade.id}`)) : undefined
                        }
                      >
                        <div className="posted-card-title">{trade.cardName}</div>
                        <div className="posted-card-meta">
                          {trade.game} · {trade.condition || "Any condition"}
                        </div>
                        <div className="posted-card-meta">
                          ZIP {trade.zipCode || "N/A"} · {trade.radius || 25} mi
                        </div>
                        <div className="posted-card-meta posted-match-meta">
                          {Number(trade.matchCount || 0)} active match{Number(trade.matchCount || 0) === 1 ? "" : "es"}
                        </div>
                        <div className="posted-card-foot">
                          <div className="posted-card-foot-left">
                            <span className={`posted-status posted-status--${displayStatus}`}>{displayStatus}</span>
                            {isBoosted && <span className="posted-status posted-status--boosted">Boosted</span>}
                            <span>
                              {trade.createdAt
                                ? new Date(trade.createdAt).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })
                                : ""}
                            </span>
                          </div>
                          <div className="posted-card-actions">
                            <button
                              type="button"
                              className="posted-delete-button posted-match-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openTradeHubForPost("trade", trade.id);
                              }}
                              disabled={!isActive}
                              title={!isActive ? "Only active posts have live matches" : undefined}
                            >
                              View matches
                            </button>
                            <button
                              type="button"
                              className="posted-delete-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteModal("trade", trade);
                              }}
                              disabled={!isActive}
                              title={!isActive ? "Only active posts can be deleted from here" : undefined}
                            >
                              Delete
                            </button>
                            {isBoosted ? (
                              <span className="posted-boost-window">
                                Boost ends: {formatBoostEndDate(trade?.boostExpiresAt)}
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="posted-delete-button posted-boost-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBoost("trade", trade.id);
                                }}
                                disabled={
                                  boostingKey === `trade:${trade.id}` ||
                                  !isActive ||
                                  (boostCheckoutOpen && boostCheckoutTarget?.postType === "trade" && String(boostCheckoutTarget?.postId) === String(trade.id))
                                }
                                title={!isActive ? "Only active posts can be boosted" : undefined}
                              >
                                {boostingKey === `trade:${trade.id}`
                                  ? "Boosting..."
                                  : boostCheckoutOpen &&
                                      boostCheckoutTarget?.postType === "trade" &&
                                      String(boostCheckoutTarget?.postId) === String(trade.id)
                                    ? "Checkout Open"
                                    : "Boost"}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}

            {!postedLoading && !postedError && postedTab === "bounties" && (
              <>
                {filteredPostedBounties.length === 0 ? (
                  <div className="empty-state">
                    {postedStatusFilter === "all"
                      ? "You haven’t posted any bounties yet."
                      : `No ${postedStatusFilter} bounties.`}
                  </div>
                ) : (
                  <div className="posted-list">
                    {filteredPostedBounties.map((bounty) => {
                      const displayStatus = getDisplayPostedStatus(bounty);
                      const isActive = isPostedItemActive(bounty);
                      const isBoosted = isActive && isPostedItemBoosted(bounty);
                      return (
                      <div
                        key={bounty.id}
                        className="posted-card posted-bounty-card"
                        onClick={() => {
                          if (!isActive) return;
                          navigate(`/bounty-offer/${bounty.id}`);
                        }}
                        role={isActive ? "button" : undefined}
                        tabIndex={isActive ? 0 : -1}
                        onKeyDown={(e) =>
                          isActive ? handlePostedCardKeyDown(e, () => navigate(`/bounty-offer/${bounty.id}`)) : undefined
                        }
                      >
                        <img
                          src={bounty.photoUrl || bountyPlaceholder}
                          alt={bounty.cardName}
                          className="posted-bounty-image"
                          onError={(e) => {
                            e.currentTarget.src = bountyPlaceholder;
                          }}
                        />
                        <div className="posted-card-content">
                          <div className="posted-card-title">{bounty.cardName}</div>
                          <div className="posted-card-meta">
                            {bounty.game} · {bounty.desiredCondition || "Any condition"}
                          </div>
                          <div className="posted-card-meta">
                            ZIP {bounty.zipCode || "N/A"} · {bounty.radius || 25} mi
                          </div>
                          <div className="posted-card-meta posted-match-meta">
                            {Number(bounty.matchCount || 0)} active match{Number(bounty.matchCount || 0) === 1 ? "" : "es"}
                          </div>
                          <div className="posted-card-foot">
                            <div className="posted-card-foot-left">
                              <span className={`posted-status posted-status--${displayStatus}`}>{displayStatus}</span>
                              {isBoosted && <span className="posted-status posted-status--boosted">Boosted</span>}
                              <span>
                                {bounty.createdAt
                                  ? new Date(bounty.createdAt).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    })
                                  : ""}
                              </span>
                            </div>
                            <div className="posted-card-actions">
                              <button
                                type="button"
                                className="posted-delete-button posted-match-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTradeHubForPost("bounty", bounty.id);
                                }}
                                disabled={!isActive}
                                title={!isActive ? "Only active posts have live matches" : undefined}
                              >
                                View matches
                              </button>
                              <button
                                type="button"
                                className="posted-delete-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteModal("bounty", bounty);
                                }}
                                disabled={!isActive}
                                title={!isActive ? "Only active posts can be deleted from here" : undefined}
                              >
                                Delete
                              </button>
                              {isBoosted ? (
                                <span className="posted-boost-window">
                                  Boost ends: {formatBoostEndDate(bounty?.boostExpiresAt)}
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="posted-delete-button posted-boost-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBoost("bounty", bounty.id);
                                  }}
                                  disabled={
                                    boostingKey === `bounty:${bounty.id}` ||
                                    !isActive ||
                                    (boostCheckoutOpen && boostCheckoutTarget?.postType === "bounty" && String(boostCheckoutTarget?.postId) === String(bounty.id))
                                  }
                                  title={!isActive ? "Only active posts can be boosted" : undefined}
                                >
                                  {boostingKey === `bounty:${bounty.id}`
                                    ? "Boosting..."
                                    : boostCheckoutOpen &&
                                        boostCheckoutTarget?.postType === "bounty" &&
                                        String(boostCheckoutTarget?.postId) === String(bounty.id)
                                      ? "Checkout Open"
                                      : "Boost"}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {activeTab === "plan" && (
          <div className="reviews-section">
            <h3 className="section-title">Plan & Usage</h3>
            {monetizationLoading && <div className="loading-state">Loading plan details...</div>}
            {!monetizationLoading && monetization && (
              <div className="plan-usage-layout">
                <div className="plan-usage-hero">
                  <div>
                    <div className="plan-usage-title">
                      {monetization.plan?.name} (${(Number(monetization.plan?.monthlyPriceCents || 0) / 100).toFixed(2)}/month)
                    </div>
                    <div className="plan-usage-subtitle">Your active subscription and usage limits</div>
                  </div>
                  <span className={`plan-usage-chip ${getSubscriptionStatusClass(monetization.subscription)}`}>
                    {getSubscriptionStatusLabel(monetization.subscription)}
                  </span>
                </div>

                <div className="plan-usage-grid">
                  <div className="plan-usage-metric">
                    <div className="plan-usage-label">Billing</div>
                    <div className="plan-usage-value">{monetization.subscription?.provider ? "PayPal" : "Manual / In-app"}</div>
                  </div>
                  <div className="plan-usage-metric">
                    <div className="plan-usage-label">Next billing</div>
                    <div className="plan-usage-value">{formatLifecycleDate(monetization.subscription?.nextBillingAt)}</div>
                  </div>
                  <div className="plan-usage-metric">
                    <div className="plan-usage-label">Downgrade effective</div>
                    <div className="plan-usage-value">{formatLifecycleDate(monetization.subscription?.downgradeEffectiveAt)}</div>
                  </div>
                  <div className="plan-usage-metric">
                    <div className="plan-usage-label">Bounties</div>
                    <div className="plan-usage-value">
                      {monetization.usage?.activeBounties} / {monetization.plan?.maxActiveBounties}
                    </div>
                  </div>
                  <div className="plan-usage-metric">
                    <div className="plan-usage-label">Trades</div>
                    <div className="plan-usage-value">
                      {monetization.usage?.activeTrades} / {monetization.plan?.maxActiveTrades}
                    </div>
                  </div>
                  <div className="plan-usage-metric">
                    <div className="plan-usage-label">Free boosts remaining</div>
                    <div className="plan-usage-value">
                      {monetization.usage?.freeBoostsRemaining} / {monetization.plan?.monthlyFreeBoosts}
                    </div>
                  </div>
                  <div className="plan-usage-metric">
                    <div className="plan-usage-label">Paid boost</div>
                    <div className="plan-usage-value">
                      ${(Number(monetization.plan?.paidBoostPriceCents || 0) / 100).toFixed(2)}
                    </div>
                  </div>
                  <div className="plan-usage-metric">
                    <div className="plan-usage-label">Post lifetime</div>
                    <div className="plan-usage-value">{monetization.plan?.postLifetimeDays} days</div>
                  </div>
                  <div className="plan-usage-metric">
                    <div className="plan-usage-label">Boost extension</div>
                    <div className="plan-usage-value">{monetization.plan?.boostExtensionDays} days</div>
                  </div>
                </div>

                {monetization.subscription?.cancelAtPeriodEnd && (
                  <div className="plan-usage-alert plan-usage-alert-warning">
                    Your downgrade is scheduled for {formatLifecycleDate(monetization.subscription?.downgradeEffectiveAt)}.
                    Paid plan benefits stay active until then.
                  </div>
                )}
                {["past_due", "suspended", "cancelled", "expired"].includes(
                  String(monetization.subscription?.subscriptionStatus || "").toLowerCase(),
                ) && !monetization.subscription?.cancelAtPeriodEnd && (
                  <div className="plan-usage-alert plan-usage-alert-danger">
                    Subscription status: {getSubscriptionStatusLabel(monetization.subscription)}.
                    {monetization.subscription?.cancelledAt
                      ? ` Updated ${formatLifecycleDate(monetization.subscription?.cancelledAt)}.`
                      : ""}
                  </div>
                )}

                <div className="plan-usage-entitlements">
                  <div className="posted-card-meta">
                    Badges: {(monetization.badges || []).map((b) => b.badgeType).join(", ") || "None"}
                  </div>
                  <div className="posted-card-meta">
                    Themes: {monetization.plan?.themesEnabled ? "Enabled" : "Not included"}
                  </div>
                  <div className="posted-card-meta">
                    Last payment: {formatLifecycleDate(monetization.subscription?.lastPaymentAt)}
                  </div>
                </div>

                {monetization.plan?.themesEnabled && (
                  <div className="posted-card">
                    <div className="posted-card-title">Profile Theme</div>
                    <div className="posted-card-foot" style={{ marginTop: 10 }}>
                      <button className="btn-secondary" disabled={themeSaving} onClick={() => handleThemeChange("pokemon")}>
                        Pokemon
                      </button>
                      <button className="btn-secondary" disabled={themeSaving} onClick={() => handleThemeChange("mtg")}>
                        MTG
                      </button>
                      <button className="btn-secondary" disabled={themeSaving} onClick={() => handleThemeChange("one-piece")}>
                        One Piece
                      </button>
                    </div>
                  </div>
                )}
                <div className="posted-cta-wrap">
                  <button type="button" className="btn-primary" onClick={() => navigate("/onboarding/plan?mode=manage")}>
                    Change Plan
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* PHASE 2: Verification Modal - DORMANT */}
      {/* 
      {showVerificationModal && (
        <VerificationModal
          user={user}
          onClose={() => setShowVerificationModal(false)}
          onSubmit={handleVerificationSubmit}
        />
      )}
      */}
      </div>
      <LocationPickerModal
        isOpen={profileLocationModalOpen}
        title="Profile Location"
        subtitle="Update your home market. We’ll ask what should happen to active posts next."
        confirmLabel="Continue relocation"
        initialLocation={profileLocation}
        onClose={() => setProfileLocationModalOpen(false)}
        onConfirm={async (location) => {
          setProfileLocationModalOpen(false);
          await handleProfileLocationPicked(location);
        }}
      />
      <LocationPickerModal
        isOpen={browsingLocationModalOpen}
        title="Browsing Location"
        subtitle="Change where discovery happens without moving your existing posts."
        confirmLabel="Save browsing area"
        initialLocation={browsingLocation}
        initialRadiusMiles={browsingRadiusMiles}
        showRadius
        onClose={() => setBrowsingLocationModalOpen(false)}
        onConfirm={async (location, radiusMiles) => {
          await handleBrowsingSave(location, radiusMiles);
          setBrowsingLocationModalOpen(false);
        }}
      />
      {relocationModalOpen && (
        <div className="modal-overlay" onClick={() => !relocationSaving && setRelocationModalOpen(false)}>
          <div className="modal-content" onClick={(event) => event.stopPropagation()}>
            <h2>Relocate Account</h2>
            <p>Profile location will change to {formatLocationLabel(pendingProfileLocation)}.</p>
            <div className="location-actions" style={{ marginBottom: 16 }}>
              <button type="button" className={`btn-secondary ${relocationMode === "move_all" ? "active" : ""}`} onClick={() => setRelocationMode("move_all")}>
                Move all active posts
              </button>
              <button type="button" className={`btn-secondary ${relocationMode === "keep_current" ? "active" : ""}`} onClick={() => setRelocationMode("keep_current")}>
                Keep current post locations
              </button>
              <button type="button" className={`btn-secondary ${relocationMode === "select_posts" ? "active" : ""}`} onClick={() => setRelocationMode("select_posts")}>
                Choose posts to move
              </button>
            </div>

            {relocationMode === "select_posts" && (
              <div style={{ display: "grid", gap: 12, maxHeight: 320, overflow: "auto", marginBottom: 16 }}>
                {activeRelocationTrades.map((item) => (
                  <label key={`trade-${item.id}`} className="filter-check-control">
                    <input
                      type="checkbox"
                      checked={relocationSelection.tradeIds.includes(item.id)}
                      onChange={() => toggleRelocationSelection("tradeIds", item.id)}
                    />
                    <span>Trade: {item.cardName}</span>
                  </label>
                ))}
                {activeRelocationBounties.map((item) => (
                  <label key={`bounty-${item.id}`} className="filter-check-control">
                    <input
                      type="checkbox"
                      checked={relocationSelection.bountyIds.includes(item.id)}
                      onChange={() => toggleRelocationSelection("bountyIds", item.id)}
                    />
                    <span>Bounty: {item.cardName}</span>
                  </label>
                ))}
                {activeRelocationListings.map((item) => (
                  <label key={`listing-${item.id}`} className="filter-check-control">
                    <input
                      type="checkbox"
                      checked={relocationSelection.listingIds.includes(item.id)}
                      onChange={() => toggleRelocationSelection("listingIds", item.id)}
                    />
                    <span>Marketplace: {item.title}</span>
                  </label>
                ))}
                {activeRelocationTrades.length === 0 &&
                  activeRelocationBounties.length === 0 &&
                  activeRelocationListings.length === 0 && (
                    <p>No active posts to move.</p>
                  )}
              </div>
            )}

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setRelocationModalOpen(false)} disabled={relocationSaving}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={() => void submitRelocation()} disabled={relocationSaving}>
                {relocationSaving ? "Saving..." : "Apply relocation"}
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteModalOpen && (
        <div className="posted-delete-modal-backdrop" onClick={closeDeleteModal}>
          <div className="posted-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Delete listing?</h4>
            <p>
              This will remove{" "}
              <strong>{deleteTarget?.title || "this listing"}</strong> from your posted listings.
            </p>
            {deleteError && <div className="posted-delete-error">{deleteError}</div>}
            <div className="posted-delete-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeDeleteModal}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="posted-delete-confirm"
                onClick={confirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      <PaypalBoostModal
        isOpen={boostCheckoutOpen}
        target={boostCheckoutTarget}
        quote={boostCheckoutQuote}
        onClose={closeBoostCheckout}
        onSuccess={handleBoostCheckoutSuccess}
      />
      <AvatarEditModal
        isOpen={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
        onSave={handleAvatarSave}
      />
    </>
  );
}
